"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.AtomicDatePickerInput = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _core = require("@emotion/core");
var _web = require("@react-hookz/web");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _focusMode = require("../../../focusMode");
var _types = require("../../types");
var _DatePickerTextInputStyled = require("../styled/DatePickerTextInputStyled");
var _DatePickerTextInputWrap = require("../styled/DatePickerTextInputWrap");
var _utils = require("../utils");
var _DatePickerInputCalendarButton = require("./DatePickerInputCalendarButton");
var _DatePickerTextInputPlaceholder = require("./DatePickerTextInputPlaceholder");
const AtomicDatePickerInput = /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  let {
    calendarButtonProps,
    className,
    clientId,
    dayIsDisabled,
    format,
    isDisabled = false,
    isReadOnly = false,
    maxDate,
    minDate,
    isRequired,
    onChange,
    onValidationChange,
    value,
    isCompact = true,
    inputFieldHeight,
    ...rest
  } = _ref;
  const initialValue = value ? (0, _utils.dateToInputString)(value, format) : "";
  const [date, setDate] = (0, _react.useState)(initialValue);
  const [dateValid, setDateValid] = (0, _react.useState)(true);
  const [placeholder, setPlaceholder] = (0, _react.useState)(initialValue);
  const [hasBlurred, setHasBlurred] = (0, _react.useState)(false);
  const [isFocused, setIsFocused] = (0, _react.useState)(false);
  const {
    focusMode
  } = (0, _focusMode.useFocusMode)();
  const validationState = dateValid ? undefined : "error";
  const onDateChange = val => {
    /**
     * Here we're making sure what you typed is a valid character
     * e.g. numbers and delimiters
     */
    let newValue = val.replace(_types.INPUT_REGEX, "");
    if (date.length <= newValue.length) {
      newValue = (0, _utils.inputHelper)(newValue, format);
    }
    setPlaceholder(newValue);
    const validationResult = (0, _utils.validateInput)(newValue, format, minDate, maxDate, dayIsDisabled, isRequired);
    const isValid = validationResult === "isValid";
    if (hasBlurred || newValue.length === format.length || newValue.length === 0 && isRequired) {
      onValidationChange(validationResult, newValue);
      setDateValid(isValid);
    }
    if (isValid) {
      onChange((0, _utils.inputStringToDate)(newValue, format));
      setDate(newValue);
    } else if (newValue.length === 0) {
      setDate(newValue);
      onChange(null);
    } else {
      /**
       * handles deletion of characters first (old value vs new value length),
       * then moves to checking for stuff we can auto-insert
       */
      if (date.length > newValue.length) {
        setDate(newValue);
      } else {
        setPlaceholder(newValue);
        setDate(newValue);
      }
    }
  };
  const onBlurHandler = () => {
    setHasBlurred(true);
    setIsFocused(false);
    if (date.trim().length > 0 || isRequired) {
      const validationResult = (0, _utils.validateInput)(date, format, minDate, maxDate, dayIsDisabled, isRequired);
      onValidationChange(validationResult, date);
      setDateValid(validationResult === "isValid");
    } else {
      // clear error messages if the input is empty
      setDateValid(true);
      onValidationChange("isValid");
    }
  };
  const onCalendarClick = e => {
    e.stopPropagation();
    calendarButtonProps.onClick?.(e);
  };
  const handleSomeValidChanges = (0, _react.useCallback)((validationResult, dateString) => {
    if (validationResult === "isValid") {
      setDateValid(true);
      onValidationChange("isValid");
    } else {
      setDateValid(false);
      onValidationChange(validationResult, dateString);
    }
  }, [onValidationChange]);
  const prevValue = (0, _web.usePrevious)(value);
  (0, _react.useEffect)(() => {
    if (prevValue !== value) {
      if (value) {
        const dateString = (0, _utils.dateToInputString)(value, format);
        const validationResult = (0, _utils.isDateBetweenDates)(value, minDate, maxDate);
        setPlaceholder(dateString);
        setDate(dateString);
        handleSomeValidChanges(validationResult, dateString);
      } else {
        setDate("");
        setDateValid(true);
        onValidationChange("isValid");
        setPlaceholder("");
      }
    }
  }, [format, maxDate, minDate, onValidationChange, prevValue, value, handleSomeValidChanges]);
  (0, _react.useEffect)(() => {
    if (prevValue !== undefined && prevValue !== value && (value || isRequired)) {
      const dateString = value ? (0, _utils.dateToInputString)(value, format) : "";
      const validationResult = (0, _utils.validateInput)(dateString, format, minDate, maxDate, dayIsDisabled, isRequired);
      handleSomeValidChanges(validationResult, dateString);
    }
  }, [handleSomeValidChanges, prevValue, value, format, minDate, maxDate, dayIsDisabled, isRequired, date]);
  const prevFormat = (0, _web.usePrevious)(format);
  (0, _react.useEffect)(() => {
    if (prevFormat && prevFormat !== format && value) {
      const date = (0, _utils.dateToInputString)(value, format);
      setDate(date);
      setPlaceholder(date);
    }
  }, [format, prevFormat, value]);
  return /*#__PURE__*/_react.default.createElement(_core.ClassNames, null, _ref2 => {
    let {
      cx
    } = _ref2;
    return /*#__PURE__*/_react.default.createElement(_DatePickerTextInputWrap.DatePickerTextInputWrap, null, /*#__PURE__*/_react.default.createElement(_DatePickerTextInputPlaceholder.DatePickerTextInputPlaceholder, {
      clientId: clientId,
      format: format,
      value: placeholder,
      validationState: validationState,
      focusMode: focusMode,
      isFocused: isFocused,
      inputFieldHeight: inputFieldHeight
    }), /*#__PURE__*/_react.default.createElement(_DatePickerTextInputStyled.DatePickerTextInputStyled, (0, _extends2.default)({
      inputFieldHeight: inputFieldHeight,
      className: cx(className, {
        "compact-dp-input": isCompact
      }),
      clientId: (0, _lodestarUtils.getClientId)(clientId, "dp-input-input"),
      value: date,
      onChange: onDateChange,
      onBlur: onBlurHandler,
      onFocus: () => setIsFocused(true),
      contentAfter: /*#__PURE__*/_react.default.createElement(_DatePickerInputCalendarButton.DatePickerInputCalendarButton, (0, _extends2.default)({
        inputFieldHeight: inputFieldHeight
      }, calendarButtonProps, {
        onClick: onCalendarClick,
        isDisabled: isDisabled,
        isReadOnly: isReadOnly,
        clientId: clientId
      })),
      validationState: validationState,
      readOnly: isReadOnly,
      isDisabled: isDisabled,
      ref: ref
    }, rest)));
  });
});
exports.AtomicDatePickerInput = AtomicDatePickerInput;
AtomicDatePickerInput.displayName = "AtomicDatePickerInput";
//# sourceMappingURL=AtomicDatePickerInput.js.map